import React, { useEffect, useState } from "react"
import { Container, Row, Col, Tabs, Tab, Table, Card, Badge } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Link, useLocation, useParams } from "react-router-dom"
import Swal from "sweetalert2"
import { readSinester, editSinester, getInvolvedTypes } from "../actions/other/sinestersActions"
import { RootState, AppDispatch } from "../store"
import mediosDeclaracion from "../components/utils/mediosDeclaracion"
import SinesterForm from "../components/forms/SinesterForm"
import { ArrowLeft, MoveRight } from "lucide-react"
import Loader from "../components/loaders/Loader"

export interface Sinester {
	_id?: string
	id?: string
	refCia?: string
	abrevCia?: string
	tomador?: string
	compania?: string
	fecha?: string
	fechaApertura?: string
	fechaCierre?: string
	fechaEfecto?: string
	estado?: string
	descripcion?: string
	descripcionProducto?: string
	arrayNotes?: any[]
	arrayImplicados?: any[]
}

export interface Implicado {
	name: string;
	description: string;
}

export default function SinesterDetail() {
	const { t } = useTranslation()
	const { id } = useParams()
	const location = useLocation()
	const dispatch: AppDispatch = useDispatch()

	const { sinester, loadingdata, resultsinester } = useSelector((state: RootState) => state.sinesters)

	const [isEditing, setIsEditing] = useState(false)
	const [sinesterSelected, setSinesterSelected] = useState<Sinester | null>(null)
	const { involvedTypes } = useSelector((state: RootState) => state.sinesters)

	useEffect(() => {
		if (sinester) {
			setSinesterSelected(sinester)
			dispatch(getInvolvedTypes() as any)
		}
	}, [sinester, dispatch])

	useEffect(() => {
		if (id) {
			dispatch(readSinester(id) as any)
		}
	}, [id, dispatch])

	const handleInputChange = (e: any) => {
		const { name, value } = e.target
		setSinesterSelected(prev =>
			prev ? { ...prev, [name]: value } : null
		)
	}

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		setIsEditing(true)
		dispatch(editSinester(sinesterSelected, sinesterSelected?.id) as any)
	}

	useEffect(() => {
		if (!resultsinester) return

		dispatch({ type: "SINESTER_RESET_RESULT" })
		switch (resultsinester) {
			case "editsuccess":
				// Recargar para obtener _id/ebrokerId asignados por el backend.
				// Sin esto la nota sigue pareciendo editable y se puede reenviar a ebroker.
				if (id) {
					dispatch(readSinester(id) as any)
				}
				Swal.fire({
					icon: "success",
					confirmButtonText: t("global.great"),
					text: t("sinesterTranslate.editSuccess"),
				}).then(() => {
					setIsEditing(false)
				})
				break

			case "needMoreData":
				Swal.fire({
					icon: "warning",
					confirmButtonText: t("global.ok"),
					text: t("sinesterTranslate.needMoreData"),
				})
				break
		}
	}, [resultsinester, dispatch, t, id])

	const pathParts = location.pathname.split("/")
	const base = pathParts[1]

	return (
		<Container fluid className="p-1 py-5 p-md-5">
			<Row>
				<Col>
					<h1>{t("sinesterTranslate.sinesterDetails")}</h1>
				</Col>
			</Row>
			<Row className="my-3">
				<Col>
					<Tabs
						defaultActiveKey="general"
						id="client-tabs"
						className="mb-0"
						fill
					>
						<Tab eventKey="general" title={t('sinesterTranslate.sinester')}>
							<div className="border border-top-0 rounded-bottom p-4 bg-white">
								{loadingdata
									?
									<Loader />
									:
									<Row className="my-1">
										<Col xs={12} className="mb-3 d-flex align-items-center justify-content-between gap-3">
											<Badge bg="secondary">{sinester?.estado}</Badge>
											<div className="d-flex align-items-center gap-3">
												<Link to={`/${base}/clientes/${sinester?.idCliente}`} className="btn btn-secondary">
													{t("sinesterTranslate.viewClient")} <MoveRight size={16} />
												</Link>
												<Link to={`/${base}/polizas/${sinester?.idPoliza}`} className="btn btn-secondary">
													{t("sinesterTranslate.viewPolicy")} <MoveRight size={16} />
												</Link>
											</div>
										</Col>
										<Col xs={12} lg={3} className="mb-3">
											<h5>{t("sinesterTranslate.idSiniestro")}</h5>
											<hr />
											{sinester?.id || "—"}
										</Col>
										<Col xs={12} lg={3} className="mb-3">
											<h5>{t("sinesterTranslate.refCia")}</h5>
											<hr />
											{sinester?.referenciaCia || "—"}
										</Col>
										<Col xs={12} lg={3} className="mb-3">
											<h5>{t("sinesterTranslate.compania")}</h5>
											<hr />
											{sinester?.compania || "—"}
										</Col>
										<Col xs={12} lg={3} className="mb-3">
											<h5>{t("sinesterTranslate.subEstado")}</h5>
											<hr />
											{sinester?.subEstado || "—"}
										</Col>
										<Col xs={12} lg={3} className="mb-3">
											<h5>{t("sinesterTranslate.tipoSiniestro")}</h5>
											<hr />
											{sinester?.tipoSiniestro || "—"}
										</Col>
										<Col xs={12} lg={3} className="mb-3">
											<h5>{t("sinesterTranslate.medioDeclaracion")}</h5>
											<hr />
											{mediosDeclaracion.find(m => m.id === sinester?.medioDeclaracion)?.value || "—"}
										</Col>
										<Col xs={12} lg={3} className="mb-3">
											<h5>{t("sinesterTranslate.riesgo")}</h5>
											<hr />
											{sinester?.descripcionProducto || "—"}
										</Col>
										<Col xs={12} lg={3} className="mb-3">
											<h5>{t("sinesterTranslate.culpa")}</h5>
											<hr />
											{sinester?.culpa || "—"}
										</Col>
										<Col xs={12} lg={3} className="mb-3">
											<h5>{t("sinesterTranslate.fecha")}</h5>
											<hr />
											{sinester?.fecha
												? new Intl.DateTimeFormat("es-ES", {
													year: "numeric",
													month: "2-digit",
													day: "2-digit",
												}).format(new Date(sinester.fecha))
												: "—"}
										</Col>
										<Col xs={12} lg={3} className="mb-3">
											<h5>{t("sinesterTranslate.fechaApertura")}</h5>
											<hr />
											{sinester?.fechaApertura
												? new Intl.DateTimeFormat("es-ES", {
													year: "numeric",
													month: "2-digit",
													day: "2-digit",
												}).format(new Date(sinester.fechaApertura))
												: "—"}
										</Col>
										<Col xs={12} lg={3} className="mb-3">
											<h5>{t("sinesterTranslate.fechaCierre")}</h5>
											<hr />
											{sinester?.fechaCierre
												? new Intl.DateTimeFormat("es-ES", {
													year: "numeric",
													month: "2-digit",
													day: "2-digit",
												}).format(new Date(sinester.fechaCierre))
												: "—"}
										</Col>
										<Col xs={12} className="mb-3">
											<h5>{t("sinesterTranslate.descripcion")}</h5>
											<hr />
											{sinester?.descripcion || "—"}
										</Col>
										{sinester?.auto && (
											<>
												<Col xs={12} lg={6} className="mb-3">
													<h5>{t("sinesterTranslate.matricula")}</h5>
													<hr />
													{sinester.auto.matricula || "—"}
												</Col>
												<Col xs={12} lg={6} className="mb-3">
													<h5>{t("sinesterTranslate.fechaPeritacion")}</h5>
													<hr />
													{sinester?.datosPeritacion?.fechaPeritacion
														? new Intl.DateTimeFormat("es-ES", {
															year: "numeric",
															month: "2-digit",
															day: "2-digit",
														}).format(new Date(sinester.datosPeritacion.fechaPeritacion))
														: "—"}
												</Col>
											</>
										)}
									</Row>
								}
							</div>
						</Tab>
						{sinester?.arrayImplicados?.length > 0 && (
							<Tab eventKey="implicados" title={t('sinesterTranslate.involved')}>
								<div className="border border-top-0 rounded-bottom p-4 bg-white">
									{Object.entries(
										sinester.arrayImplicados.reduce((groups: Record<string, any[]>, implicado: any) => {
											const tipo = implicado.idTipoImplicado || t('sinesterTranslate.noType')
											if (!groups[tipo]) groups[tipo] = []
											groups[tipo].push(implicado)
											return groups
										}, {})
									).map(([tipo, implicados]) => {
										const tipoNombre = (involvedTypes as any[])?.find(
											(t: any) => String(t.ebrokerId) === String(tipo)
										)?.name || tipo

										return (
											<div key={tipo} className="mb-4">
												<h4 className="mt-2 mb-3">{tipoNombre}</h4>
												<Row>
													{(implicados as any[]).map((implicado: any, index: number) => (
														<Col key={index} xs={12} md={6} lg={4} className="mb-3">
															<Card className="h-100 shadow-sm">
																<Card.Body>
																	<Row>
																		<Col xs={6} className="mb-3">
																			<h5>{t('sinesterTranslate.fullName')}</h5>
																			<hr />
																			{implicado.nombreCompleto || '—'}
																		</Col>
																		<Col xs={6} className="mb-3">
																			<h5>{t('sinesterTranslate.documentation')}</h5>
																			<hr />
																			{implicado.documentacion || '—'}
																		</Col>
																		<Col xs={6} className="mb-3">
																			<h5>{t('sinesterTranslate.matricula')}</h5>
																			<hr />
																			{implicado.matricula || '—'}
																		</Col>
																		<Col xs={6} className="mb-3">
																			<h5>{t('sinesterTranslate.compania')}</h5>
																			<hr />
																			{implicado.descripcionCompania || '—'}
																		</Col>
																		<Col xs={6}>
																			<h5>{t('sinesterTranslate.culpa')}</h5>
																			<hr />
																			{implicado.culpa || '—'}
																		</Col>
																	</Row>
																</Card.Body>
															</Card>
														</Col>
													))}
												</Row>
											</div>
										)
									})}
								</div>
							</Tab>
						)}
						<Tab eventKey="comentarios" title={t('policyTranslate.comments')}>
							<div className="border border-top-0 rounded-bottom px-1 py-2 bg-white">
								<SinesterForm
									state={sinesterSelected}
									handleInputChange={handleInputChange}
									submit={handleSubmit}
									isEditing={isEditing}
									loadingdata={loadingdata}
								/>
							</div>
						</Tab>
					</Tabs>
				</Col>
			</Row>
		</Container>
	)
}
